<template>
  <v-container id="contentView">
    <v-card>
      <v-card-title class="mb-0">
        {{ $t('content.content-library') }}
      </v-card-title>
      <v-progress-linear v-if="loading" indeterminate />
      <v-card-text class="mt-5">
        <v-select
        v-model="selectedSort"
        :items="sortOptions.map((x) => { return { ...x, text: $t(x.text) } })"
        item-text="text"
        item-value="value"
        label="Sort by"
        dense
      />
        <v-container v-if="!loading">
          <v-row v-if="sortedItems.length > 0">
            <v-col v-for="item in sortedItems" :key="item.id" cols="12" sm="6" md="4" class="d-flex flex-column">
              <module-card
                :model="item"
                available
                class="mb-1 flex-grow-1"
                @edit="editItem(item)"
              />
            </v-col>
          </v-row>
          <v-row v-else>
            <v-col>{{ $t('content.no-available-modules') }}</v-col>
          </v-row>
        </v-container>
        <v-container v-if="loading">
          {{ $t('global.loading') }}
        </v-container>
      </v-card-text>
    </v-card>
  </v-container>
</template>


<script>

  import api from '@/helpers/api.js'
  import moduleCard from './ModuleCard.vue'
  import { ContentLibrary } from '@/helpers/contentLibrary.js'
  import sessionsAPI from '@/helpers/sessions.js'
  
  export default {
    name: 'Content',

    components: {
      'module-card': moduleCard,
    },

    props: {
    },

    data () {
      return {
        loading: false,
        selected: [],
        certificates: [],
        sessions: [],
        progressValues: {},
        submissionReports: [],
        items: [],
        sortOptions: [
          { value: 'nameAsc', text: 'moduleBrowser.alphabetical-ascending' },
          { value: 'nameDesc', text: 'moduleBrowser.alphabetical-descending' },
          { value: 'completion', text: 'moduleBrowser.completion-descending' },
          // { value: 'releaseDate', text: 'Release Date' },
          // Add other sorting options here...
        ],
        selectedSort: 'completion', // Default sort option
      }
    },

    computed: {
      unlockedItems: function () {
        return this.items.filter((x) => x.purchased)
      },
      lockedItems: function () {
        return this.items.filter((x) => !x.purchased && x.public)
      },
      showUnavailable: function () {
        return false // this.$store.getters.currentUserProfile.user.roles.includes('admin')
      },
      sortedItems: function () {
        // Create a copy of the unlockedItems array
        const itemsCopy = [...this.unlockedItems]
        // Sort the copy of the array
        return itemsCopy.sort((a, b) => {
          switch (this.selectedSort) {
            case 'nameAsc':
              return this.getItemTitle(a).localeCompare(this.getItemTitle(b))
            case 'nameDesc':
              return this.getItemTitle(b).localeCompare(this.getItemTitle(a))
            // Add other cases for different sort options...
            case 'completion': {
              const progressDiff = (b.progress || 0) - (a.progress || 0)
              if (progressDiff !== 0) {
                return progressDiff
              }
              // If progress is the same, sort alphabetically by title
              return this.getItemTitle(a).localeCompare(this.getItemTitle(b))
            }
            case 'releaseDate':
              return b.releaseDate - a.releaseDate
            default:
              return 0
          }
        })
      },
    },

    async mounted () {
      await this.refreshItems()
    },

    methods: {
      getItemTitle (item) {
        return item.locales?.[this.$i18n.locale]?.title ?? item.locales?.en?.title ?? item.uid ?? item.id
      },
      constructLearnerReportURL (submissionReportId) {
        return `${process.env.VUE_APP_URL}/learner-report/${submissionReportId}`
      },
      // TODO: remove this
      duration (seconds) {
        return Math.ceil(seconds / 60) + ' minutes'
      },
      available (item) {
        return item.purchased
      },
      async refreshItems (force = false) {
        this.loading = true
        
        // Parallelize the main API calls
        const [contents, sessions, submissionReports] = await Promise.all([
          ContentLibrary.Get(this.$store, this.$i18n.locale),
          this.$store.getters.currentUserProfile.user.id 
            ? api.getSessionsByUser(this.$store.getters.currentUserProfile.user.id)
            : Promise.resolve([]),
          this.$store.getters.currentUserProfile.user.id
            ? api.getSubmissionReportsByUser(this.$store.getters.currentUserProfile.user.id)
            : Promise.resolve([]),
        ])
        
        this.sessions = sessions

        // the contents for bodyswaps.jobInterviews.practice2
        
        // filter the submissionReports only if they include isSubmitted = true, handle cases where this field is not present or the submission report is []
        this.submissionReports = Array.isArray(submissionReports.data) 
          ? submissionReports.data.filter((x) => x.isSubmitted)
          : []

        this.items = contents === null ? [] : contents.filter((x) => { 
          // return ([ContentLibrary.ContentType.SIMULATOR].includes(x.type)) || (x.purchased && [ContentLibrary.ContentType.MODULE].includes(x.type))
          return x.purchased && [ContentLibrary.ContentType.MODULE, ContentLibrary.ContentType.SIMULATOR].includes(x.type)
        })
        
        for (const session of this.sessions) {
          // this.progressValues[session.id] = this.calculateSessionProgress(session)
          session.progress = this.calculateSessionProgress(session)
          // console.log(session.module, 'session', session.progress, session)
          if (session.completed) {
            const completedModule = contents.find(module => module.uid === session.module)
            if (completedModule !== undefined) completedModule.certificate = session.id
          }
        }

        // Add reports array to each item and calculate highest progress
        for (const item of this.items) {
          const itemSessions = this.sessions.filter(session => session.module === item.id)
          item.progress = itemSessions.length > 0 
            ? Math.max(...itemSessions.map(session => session.progress))
            : 0  
          // check if this module is IPE special case it uses the session ID to retreive the learner-report (inside portal)
          // every other module uses the new style submission reports which are based on the submission id
          if (item.uid === 'IPE') {
            // map the sessionReports instead to the item.reports
            item.reports = itemSessions.map(session => ({
              date: session.updated,
              path: session.id, // This will be used as the path for accessing the report
            }))
            // Calculate highest progress from all sessions
          } else {
            const itemSubmissionReports = this.submissionReports.filter(report => report.contentId === item.id)
            item.reports = itemSubmissionReports.map(report => ({
              date: report.updatedAt,
              path: this.constructLearnerReportURL(report.id), // This will be used as the path for accessing the report
            })) 
          } 
        }

        this.loading = false
      },
      async editItem (item) {
        // this.loading = true
        this.editedItem = item.uid
        this.showEditDialog = true
        // this.loading = false
      },
      calculateSessionProgress (item) {        
        const moduleInfo = this.items.find(x => x.id === item.module)
        const progress = sessionsAPI.calculateProgressFromSession(moduleInfo, item)
        this.$set(this.progressValues, item.id, progress)
        return progress
      },
    },
  }
</script>

<style>
.gridcontainer {
  width: 100%;
  margin: 0 auto;
  vertical-align: top;
}

.gridcontent {

  display: inline-block;
  margin: 10px;
  vertical-align: top;
}

.statusbar {
  background-color:darkgrey;
  padding: 10px;
  color: white;
  margin-bottom: 10px;
}

.purchased {
  background-color: #ff9416;
}

.localeChip {
  background-color: rgb(79, 94, 100) !important;
  text-transform: uppercase;
  height: 16px;
  padding: 8px;
  color: white !important;
}

.contentTitle {
    display: inline-block;
    word-break: break-word;
    line-height: 1.1;
}

.icon-green {
  color: green;
}

.greyout {
  opacity: 0.4;
  filter: alpha(opacity=40);
}
</style>
